import { Button, Form, Input, Modal, Upload } from 'antd';
import { FC, useCallback, useState } from 'react';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { useFileUpload } from 'hooks';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DicomShareRow, createDicomShare } from 'services/api/dicomShares';

import { showRestError } from 'utils/showErrorMessage';
import { Loading } from 'components/ui';
import { CopyLinkPassword } from 'components/shared';

interface IProps {
  onClose: () => void;
  onCreated?: () => void;
}

const UploadDicom: FC<IProps> = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [showResult, setShowResult] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [percent, setPercent] = useState(0);
  const [result, setResult] = useState<DicomShareRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const {
    result: file,
    upload,
    onCancel
  } = useFileUpload({
    handleResult: true
  });

  const handleSubmit = useCallback(
    (values: { name: string }) => {
      setLoading(true);
      createDicomShare({ ...values, file })
        .then(row => {
          setResult(row);
          setShowResult(true);
          form.resetFields();
          onCreated?.();
        })
        .catch(err => showRestError(err))
        .finally(() => setLoading(false));
    },
    [file, form, onCreated]
  );

  const onUploadFile = async (e: UploadRequestOption) => {
    try {
      setUploading(true);
      const res = await upload(e.file as File, {
        onUploadProgress({ progress: value }) {
          setPercent(value || 0);
        }
      });

      e.onSuccess?.(res);
    } catch (err) {
      e.onError?.(err as ProgressEvent<EventTarget>);
      setPercent(0);
    } finally {
      setUploading(false);
    }
  };

  const link = result
    ? `${result.redirect_url}?sessionId=${result.session_id}`
    : '';

  return (
    <Modal
      onCancel={() => !isUploading && onClose()}
      footer={null}
      title={t('dicom.upload_and_share')}
      closable={!isUploading}
      open
    >
      {loading ? (
        <Loading />
      ) : showResult && result ? (
        <CopyLinkPassword link={link} />
      ) : (
        <div style={{ position: 'relative' }}>
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
          >
            <Form.Item name="name" label="Title" rules={[{ required: true }]}>
              <Input maxLength={255} />
            </Form.Item>
            <Form.Item
              valuePropName="fileList"
              getValueProps={value => value}
              rules={[{ required: false }]}
            >
              <Upload.Dragger
                name="file"
                accept="application/zip"
                customRequest={onUploadFile}
                onRemove={f => onCancel(f.uid)}
                maxCount={1}
                disabled={isUploading}
                showUploadList={!isUploading}
                listType="picture"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{t('sessions.upload_file')}</p>
                <p className="ant-upload-hint">{t('messages.only_zip')}</p>
              </Upload.Dragger>
            </Form.Item>
            {isUploading && (
              <>
                {percent === 100 ? (
                  <Loading type="static" />
                ) : (
                  <Loading type="static" percent={percent} />
                )}
              </>
            )}
            <Form.Item>
              <Button
                type="primary"
                onClick={form.submit}
                disabled={isUploading}
                block
              >
                {t('sessions.link_create')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}
    </Modal>
  );
};

export default UploadDicom;
